/**
 * İzin servisi — KARMA KAYNAK desteği.
 *
 * v3: gunFarki artık "bitis = işbaşı tarihi" mantığını kullanır.
 *  10 Haziran (başlangıç) → 15 Haziran (işbaşı) = 5 gün izin
 *  (Eski kayıtlardaki gunSayisi alanı saklı kalır, etkilenmez)
 */

import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc,
  query, where, orderBy, Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp, IsoTarih } from '@/types'
import { bildirimGonder } from './bildirim-service'

const COLL = 'izinTalepleri'

function tarihEtiket(iso: IsoTarih): string {
  return iso.split('-').reverse().join('.')
}

export type IzinDurumu = 'bekliyor' | 'onaylandi' | 'reddedildi' | 'iptal'
export type IzinKaynagi = 'yillik' | 'aylik' | 'maas'

export const KAYNAK_ETIKETLERI: Record<IzinKaynagi, string> = {
  yillik: 'Yıllık İzin',
  aylik: 'Aylık Hak',
  maas: 'Maaştan Kesilsin',
}

export const DURUM_ETIKETLERI: Record<IzinDurumu, string> = {
  bekliyor: 'Onay Bekliyor',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
  iptal: 'İptal Edildi',
}

export interface IzinDagilimi {
  yillik: number
  aylik: number
  maas: number
}

export interface IzinTalebi {
  id: string
  personelId: string
  baslangicTarihi: IsoTarih       // İlk izin günü
  bitisTarihi: IsoTarih           // İşbaşı (dönüş) tarihi
  gunSayisi: number
  aciklama?: string

  durum: IzinDurumu
  kaynak?: IzinKaynagi

  yillikGun?: number
  aylikGun?: number
  maasGun?: number

  sorumluAtamasi: boolean
  atayanId?: string

  onaylayanId?: string
  onayTarihi?: FsTimestamp
  redSebebi?: string

  talepTarihi: FsTimestamp

  iptalEdildi: boolean
  iptalEdenId?: string
  iptalTarihi?: FsTimestamp
  iptalSebebi?: string
}

export interface YeniTalepData {
  personelId: string
  baslangicTarihi: IsoTarih
  bitisTarihi: IsoTarih
  aciklama?: string
}

/**
 * Gün sayısı = işbaşı tarihi − ilk izin günü.
 * Örnek: 10 → 15 Haziran = 5 gün izin (15'inde işbaşı).
 */
export function gunFarki(baslangic: IsoTarih, bitis: IsoTarih): number {
  const a = new Date(baslangic + 'T00:00:00')
  const b = new Date(bitis + 'T00:00:00')
  const ms = b.getTime() - a.getTime()
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

/** İki aralık çakışıyor mu? (bitisTarihi = işbaşı, izinli değil) */
export function kesisiyor(
  a1: IsoTarih, a2: IsoTarih, b1: IsoTarih, b2: IsoTarih
): boolean {
  // a1...a2-1 ile b1...b2-1 arası izinli günler
  // İki aralığın izinli günleri çakışıyor mu?
  return a1 < b2 && b1 < a2
}

export function yillikKesilen(t: IzinTalebi): number {
  if (t.yillikGun !== undefined) return t.yillikGun
  return t.kaynak === 'yillik' ? t.gunSayisi : 0
}

export function aylikKesilen(t: IzinTalebi): number {
  if (t.aylikGun !== undefined) return t.aylikGun
  return t.kaynak === 'aylik' ? t.gunSayisi : 0
}

export function maasKesilen(t: IzinTalebi): number {
  if (t.maasGun !== undefined) return t.maasGun
  return t.kaynak === 'maas' ? t.gunSayisi : 0
}

export async function izinTalebiOlustur(data: YeniTalepData): Promise<string> {
  if (data.bitisTarihi <= data.baslangicTarihi) {
    throw new Error('İşbaşı tarihi ilk izin gününden sonra olmalı')
  }
  const gunSayisi = gunFarki(data.baslangicTarihi, data.bitisTarihi)
  if (gunSayisi < 1 || gunSayisi > 60) {
    throw new Error('Geçersiz gün sayısı (1-60 arası olmalı)')
  }

  const cakisanlar = await izinleriGetir({
    personelId: data.personelId,
    durumlar: ['bekliyor', 'onaylandi'],
  })
  for (const t of cakisanlar) {
    if (kesisiyor(data.baslangicTarihi, data.bitisTarihi, t.baslangicTarihi, t.bitisTarihi)) {
      throw new Error(
        `Bu tarihlerde mevcut izin var (${t.baslangicTarihi} - ${t.bitisTarihi})`
      )
    }
  }

  const db = getFirestoreDb()
  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    baslangicTarihi: data.baslangicTarihi,
    bitisTarihi: data.bitisTarihi,
    gunSayisi,
    durum: 'bekliyor' as IzinDurumu,
    sorumluAtamasi: false,
    talepTarihi: nowServer(),
    iptalEdildi: false,
  }
  if (data.aciklama?.trim()) docData.aciklama = data.aciklama.trim()

  const ref = await addDoc(collection(db, COLL), docData)

  // Talep bildirimi Cloud Function trigger ile gönderilir (izinTalepTrigger).
  return ref.id
}

export async function sorumluIzinAtamasi(
  data: YeniTalepData & { atayanId: string; dagilim?: IzinDagilimi; kaynak?: IzinKaynagi }
): Promise<string> {
  if (data.bitisTarihi <= data.baslangicTarihi) {
    throw new Error('İşbaşı tarihi ilk izin gününden sonra olmalı')
  }
  const gunSayisi = gunFarki(data.baslangicTarihi, data.bitisTarihi)
  if (gunSayisi < 1 || gunSayisi > 60) {
    throw new Error('Geçersiz gün sayısı')
  }

  const cakisanlar = await izinleriGetir({
    personelId: data.personelId,
    durumlar: ['bekliyor', 'onaylandi'],
  })
  for (const t of cakisanlar) {
    if (kesisiyor(data.baslangicTarihi, data.bitisTarihi, t.baslangicTarihi, t.bitisTarihi)) {
      throw new Error(
        `Bu tarihlerde mevcut izin var (${t.baslangicTarihi} - ${t.bitisTarihi})`
      )
    }
  }

  const dagilim: IzinDagilimi = data.dagilim ?? {
    yillik: 0,
    aylik: gunSayisi,
    maas: 0,
  }
  const anaKaynak: IzinKaynagi = data.kaynak ?? 'aylik'

  const db = getFirestoreDb()
  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    baslangicTarihi: data.baslangicTarihi,
    bitisTarihi: data.bitisTarihi,
    gunSayisi,
    durum: 'onaylandi' as IzinDurumu,
    kaynak: anaKaynak,
    yillikGun: dagilim.yillik,
    aylikGun: dagilim.aylik,
    maasGun: dagilim.maas,
    sorumluAtamasi: true,
    atayanId: data.atayanId,
    onaylayanId: data.atayanId,
    onayTarihi: nowServer(),
    talepTarihi: nowServer(),
    iptalEdildi: false,
  }
  if (data.aciklama?.trim()) docData.aciklama = data.aciklama.trim()

  const ref = await addDoc(collection(db, COLL), docData)

  try {
    await bildirimGonder({
      kullaniciId: data.personelId,
      tipi: 'izinOnayRed',
      aciliyet: 'normal',
      baslik: '🏖️ İzin atandı',
      mesaj: `${tarihEtiket(data.baslangicTarihi)} → ${tarihEtiket(data.bitisTarihi)} (${gunSayisi} gün) izin atandı.`,
      link: '/personel',
    })
  } catch (e) { console.warn('[izin] atama bildirimi:', e) }

  return ref.id
}

export async function izinOnayla(
  talepId: string,
  dagilim: IzinDagilimi,
  anaKaynak: IzinKaynagi,
  onaylayanId: string
): Promise<void> {
  const toplam = dagilim.yillik + dagilim.aylik + dagilim.maas
  if (dagilim.yillik < 0 || dagilim.aylik < 0 || dagilim.maas < 0) {
    throw new Error('Negatif gün olamaz')
  }

  const db = getFirestoreDb()
  const ref = doc(db, COLL, talepId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Talep bulunamadı')
  const talep = snap.data() as IzinTalebi
  if (toplam !== talep.gunSayisi) {
    throw new Error(
      `Dağılım toplamı (${toplam}) talep gün sayısı (${talep.gunSayisi}) ile uyumsuz`
    )
  }

  await updateDoc(ref, {
    durum: 'onaylandi',
    kaynak: anaKaynak,
    yillikGun: dagilim.yillik,
    aylikGun: dagilim.aylik,
    maasGun: dagilim.maas,
    onaylayanId,
    onayTarihi: Timestamp.now(),
  })

  try {
    await bildirimGonder({
      kullaniciId: talep.personelId,
      tipi: 'izinOnayRed',
      aciliyet: 'normal',
      baslik: '✅ İzin onaylandı',
      mesaj: `${tarihEtiket(talep.baslangicTarihi)} → ${tarihEtiket(talep.bitisTarihi)} (${talep.gunSayisi} gün) iznin onaylandı.`,
      link: '/personel',
    })
  } catch (e) { console.warn('[izin] onay bildirimi:', e) }
}

export async function izinReddet(
  talepId: string,
  onaylayanId: string,
  sebep?: string
): Promise<void> {
  const db = getFirestoreDb()
  const snap = await getDoc(doc(db, COLL, talepId))
  if (!snap.exists()) return
  const talep = snap.data() as IzinTalebi
  const updates: Record<string, unknown> = {
    durum: 'reddedildi',
    onaylayanId,
    onayTarihi: Timestamp.now(),
  }
  if (sebep?.trim()) updates.redSebebi = sebep.trim()
  await updateDoc(doc(db, COLL, talepId), updates)

  try {
    await bildirimGonder({
      kullaniciId: talep.personelId,
      tipi: 'izinOnayRed',
      aciliyet: 'normal',
      baslik: '❌ İzin reddedildi',
      mesaj: `${tarihEtiket(talep.baslangicTarihi)} → ${tarihEtiket(talep.bitisTarihi)} iznin reddedildi.` +
        (sebep?.trim() ? ` Sebep: ${sebep.trim()}` : ''),
      link: '/personel',
    })
  } catch (e) { console.warn('[izin] red bildirimi:', e) }
}

export async function izinIptal(
  talepId: string,
  iptalEdenId: string,
  sebep?: string
): Promise<void> {
  const db = getFirestoreDb()
  const updates: Record<string, unknown> = {
    durum: 'iptal',
    iptalEdildi: true,
    iptalEdenId,
    iptalTarihi: Timestamp.now(),
  }
  if (sebep?.trim()) updates.iptalSebebi = sebep.trim()
  await updateDoc(doc(db, COLL, talepId), updates)
}

interface IzinFiltre {
  personelId?: string
  durumlar?: IzinDurumu[]
}

export async function izinleriGetir(filtre: IzinFiltre = {}): Promise<IzinTalebi[]> {
  const db = getFirestoreDb()
  const filters = []
  if (filtre.personelId) filters.push(where('personelId', '==', filtre.personelId))
  if (filtre.durumlar && filtre.durumlar.length > 0) {
    filters.push(where('durum', 'in', filtre.durumlar))
  }
  const q = query(collection(db, COLL), ...filters, orderBy('talepTarihi', 'desc'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<IzinTalebi, 'id'>) }))
}

export async function izinTalebiGetir(talepId: string): Promise<IzinTalebi | null> {
  const db = getFirestoreDb()
  const snap = await getDoc(doc(db, COLL, talepId))
  if (!snap.exists()) return null
  return { id: snap.id, ...(snap.data() as Omit<IzinTalebi, 'id'>) }
}

export async function aktifIzinler(personelId: string): Promise<IzinTalebi[]> {
  return izinleriGetir({ personelId, durumlar: ['onaylandi'] })
}

export async function bekleyenTalepler(): Promise<IzinTalebi[]> {
  return izinleriGetir({ durumlar: ['bekliyor'] })
}
